import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { supabase } from '../supabaseClient';

export const isMissingCampaigns = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205'].includes(error?.code) || /ads_campaign|ads_campaigns/i.test(error?.message || '');

const explain = (error, fallback) => {
  if (error?.code === '42501') return 'No tienes permiso sobre este negocio (solo el dueño o un administrador).';
  if (error?.code === '22023' && error?.message) return error.message.charAt(0).toUpperCase() + error.message.slice(1) + (/[.)]$/.test(error.message) ? '' : '.');
  return fallback;
};

// Campañas de anuncios del negocio y los textos e imágenes de «Contenido» que se pueden usar. Crear un borrador no toca Meta;
// aprobarlo manda la campaña a n8n, que la crea en Meta SIEMPRE EN PAUSA.
export const useAdCampaigns = (enabled) => {
  const { activeBusinessId } = useBusinessStore();
  const [campaigns, setCampaigns] = useState([]);
  const [texts, setTexts] = useState([]);
  const [images, setImages] = useState([]);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId || !enabled) return;
    const business = String(activeBusinessId);
    const [k, c] = await Promise.all([
      supabase.from('ads_campaigns').select('id, name, status, daily_budget, city, radius_km, age_min, age_max, headline, error, meta_campaign_id, created_at').eq('business_id', business).order('created_at', { ascending: false }).limit(20),
      supabase.from('content_items').select('id, kind, subject, result, image_path, status').eq('business_id', business).eq('status', 'listo').in('kind', ['anuncio', 'imagen']).order('created_at', { ascending: false }).limit(40)
    ]);
    if (k.error) {
      const gone = isMissingCampaigns(k.error);
      setMissing(gone);
      if (!gone) toast.error(explain(k.error, 'No se pudieron leer las campañas.'));
      return;
    }
    setMissing(false);
    setCampaigns(k.data || []);
    const items = c.data || [];
    setTexts(items.filter((i) => i.kind === 'anuncio'));
    setImages(items.filter((i) => i.kind === 'imagen' && i.image_path));
  }, [activeBusinessId, enabled]);

  useEffect(() => { load(); }, [load]);
  // Mientras haya una campaña en cola o creándose, se vuelve a mirar cada 10 segundos.
  useEffect(() => {
    if (!campaigns.some((x) => x.status === 'por_publicar' || x.status === 'publicando')) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [campaigns, load]);

  const run = async (fn, ok, fallback) => {
    setBusy(true);
    const { error } = await fn();
    setBusy(false);
    if (error) { toast.error(explain(error, fallback)); return false; }
    toast.success(ok);
    await load();
    return true;
  };

  const create = (f) => run(() => supabase.rpc('ads_campaign_create', {
    p_business: String(activeBusinessId), p_name: f.name, p_text_item: f.textId, p_image_item: f.imageId, p_headline: f.headline || null,
    p_budget: Number(f.budget), p_city: f.city, p_radius: Number(f.radius), p_age_min: Number(f.ageMin), p_age_max: Number(f.ageMax)
  }), 'Borrador guardado. Revísalo y apruébalo cuando quieras.', 'No se pudo guardar el borrador.');
  const approve = (id) => run(() => supabase.rpc('ads_campaign_request', { p_id: id }), 'Listo: se está creando en Meta, en pausa.', 'No se pudo enviar la campaña.');
  const cancel = (id) => run(() => supabase.rpc('ads_campaign_cancel', { p_id: id }), 'Campaña cancelada.', 'No se pudo cancelar la campaña.');

  return { campaigns, texts, images, missing, busy, create, approve, cancel, reload: load };
};
